import { ValueLabelPair } from "@utilityTypes/ValueLabelPair"
import { matchesSelectableLabelSearch } from "@utils/helpers/selectSearch"
import { KeyboardEventHandler, ReactElement, ReactNode, useCallback, useMemo } from "react"
import ReactSelect, {
	ActionMeta,
	GroupBase,
	MultiValue,
	OptionProps,
	StylesConfig,
	ValueContainerProps,
	components,
} from "react-select"
import { Tooltip } from "@ui-lib/src"
import { MdInfoOutline } from "react-icons/md"

export type MultiSelectOption = ValueLabelPair & {
	isDisabled?: boolean
	disabledTooltip?: string
}

type MultiSelectArgs = {
	options: MultiSelectOption[]
	value: MultiValue<MultiSelectOption>
	onChange: (args: MultiValue<MultiSelectOption>, am: ActionMeta<MultiSelectOption>) => void
	required?: boolean
	error?: boolean
	warning?: boolean
	onKeyDown?: KeyboardEventHandler
	isClearable: boolean
	tabIndex?: number
	maxAmountItems?: number
	minAmountItems?: number
	placeHolderText?: string
	isDisabled?: boolean
	selectedItemsNoun?: string
	useCompactSelectionSummary?: boolean
	isSingleColumn?: boolean
}

export default function MultiSelect({
	options,
	value,
	onChange,
	required = false,
	error = false,
	warning = false,
	onKeyDown,
	isClearable,
	tabIndex,
	maxAmountItems,
	minAmountItems,
	placeHolderText,
	isDisabled = false,
	selectedItemsNoun = "items",
	useCompactSelectionSummary = false,
	isSingleColumn = false,
}: MultiSelectArgs): ReactElement {
	const selectableOptions = useMemo(
		() => options.filter((option) => option.isDisabled !== true),
		[options]
	)
	const getSelectionSummary = useCallback(
		(selectedOptions: MultiValue<MultiSelectOption>) => {
			if (selectedOptions.length === 0) {
				return undefined
			}

			if (
				selectedOptions.length === selectableOptions.length &&
				selectableOptions.length > 1
			) {
				return "All selected"
			}

			if (selectedOptions.length > 1) {
				return `${selectedOptions.length} ${selectedItemsNoun} selected`
			}

			return selectedOptions[0]?.label
		},
		[selectableOptions.length, selectedItemsNoun]
	)

	const CustomValueContainer = useCallback(
		(props: ValueContainerProps<MultiSelectOption, true, GroupBase<MultiSelectOption>>) => {
			const children = props.children as ReactNode[]
			const inputChild = children[children.length - 1]
			const displayValue =
				props.selectProps.inputValue.length > 0
					? undefined
					: getSelectionSummary(props.getValue())

			return (
				<components.ValueContainer {...props}>
					{displayValue !== undefined ? (
						<div className="max-w-full truncate text-sm">{displayValue}</div>
					) : (
						children.slice(0, -1)
					)}
					{inputChild}
				</components.ValueContainer>
			)
		},
		[getSelectionSummary]
	)

	const CustomOption = useCallback(
		(props: OptionProps<MultiSelectOption, true, GroupBase<MultiSelectOption>>) => {
			const { data } = props

			return (
				<components.Option {...props}>
					<div className="flex min-w-0 items-center justify-between gap-2">
						<span className="min-w-0 truncate">{data.label}</span>
						{data.disabledTooltip !== undefined ? (
							<Tooltip
								className={"z-[10000] bg-dipai-secondary-800/95"}
								arrowClassName={"fill-dipai-secondary-800/95"}
								customContent={
									<div className="max-w-[320px]">{data.disabledTooltip}</div>
								}
							>
								<span
									className="text-dipai-secondary-600 flex h-4 w-4 flex-none items-center justify-center"
									onClick={(event) => event.stopPropagation()}
								>
									<MdInfoOutline className="h-4 w-4" />
								</span>
							</Tooltip>
						) : null}
					</div>
				</components.Option>
			)
		},
		[]
	)

	const selectComponents = useMemo(
		() => ({
			...(useCompactSelectionSummary === true
				? { ValueContainer: CustomValueContainer }
				: {}),
			Option: CustomOption,
		}),
		[CustomOption, CustomValueContainer, useCompactSelectionSummary]
	)

	const styles = (
		stateValue: MultiValue<MultiSelectOption>
	): StylesConfig<MultiSelectOption, true, GroupBase<MultiSelectOption>> => ({
		menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
		valueContainer: (baseStyles) =>
			useCompactSelectionSummary === true
				? {
						...baseStyles,
						padding: "0px 8px",
						flexWrap: "nowrap",
						overflow: "hidden",
						height: 32,
					}
				: { ...baseStyles, padding: "0px 0px 0px 0px" },
		container: (baseStyles) => ({ ...baseStyles, padding: "0px 0px 0px 0px" }),
		control: (baseStyles, state) => {
			return {
				...baseStyles,
				minHeight: 32,
				...(useCompactSelectionSummary === true ? { height: 32 } : {}),
				borderColor:
					(stateValue.length === 0 && required) || error
						? state.menuIsOpen === true
							? "rgb(185 28 28)"
							: "rgb(239 68 68)"
						: warning
							? state.menuIsOpen === true
								? "rgb(180 83 9)"
								: "rgb(245 158 11)"
							: "#8C939D",
				boxShadow:
					state.menuIsOpen === true
						? (stateValue.length === 0 && required) || error
							? "0 0 0 1px rgb(185 28 28)"
							: warning
								? "0 0 0 1px rgb(180 83 9)"
								: "0 0 0 1px #256DB0"
						: "0",
				borderRadius: 2,
				":hover": {
					borderColor:
						(stateValue.length === 0 && required) || error
							? "rgb(185 28 28)"
							: warning
								? "rgb(180 83 9)"
								: "#256DB0",
				},
			}
		},
		input: (baseStyles) => ({
			...baseStyles,
			height: 20,
			padding: 0,
			margin: useCompactSelectionSummary === true ? 0 : baseStyles.margin,
		}),
		placeholder: (baseStyles) => ({
			...baseStyles,
			padding: 0,
			margin: useCompactSelectionSummary === true ? 0 : "0px 0px 3px 4px",
			...(useCompactSelectionSummary === true
				? {
						maxWidth: "100%",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}
				: {}),
		}),
		multiValue: (baseStyles) =>
			useCompactSelectionSummary === true
				? { ...baseStyles, display: "none" }
				: { ...baseStyles, padding: 0, margin: "0px 0px 2px 4px" },
		multiValueLabel: (baseStyles) => ({ ...baseStyles, padding: 0, margin: 0 }),
		dropdownIndicator: (baseStyles) => ({ ...baseStyles, padding: 5, margin: 0 }),
		indicatorsContainer: (baseStyles) => ({ ...baseStyles, height: 32 }),
		clearIndicator: (baseStyles) => ({ ...baseStyles, padding: 5, margin: 0 }),
		option: (baseStyles, state) => ({
			...baseStyles,
			padding: 4,
			fontSize: "14px",
			borderRadius: 2,
			border: "1px solid rgba(204, 204, 204, 0.5)",
			cursor: state.isDisabled === true ? "not-allowed" : "pointer",
			opacity: state.isDisabled === true ? 0.65 : 1,
		}),
		menuList: (baseStyles) => ({
			...baseStyles,
			gap: 4,
			gridTemplateColumns:
				isSingleColumn === false ? "repeat(auto-fit, minmax(33%, 1fr))" : "repeat(1, 1fr)",
			display: "grid",
			padding: 4,
		}),
	})

	const selectAllOption: MultiSelectOption | undefined =
		selectableOptions.length < 2 ||
		(maxAmountItems !== undefined && selectableOptions.length > maxAmountItems)
			? undefined
			: {
					value: "<SELECT_ALL>",
					label: `All ${selectedItemsNoun ?? "items"}`,
				}

	const isSelectAllSelected = () =>
		selectableOptions.length > 0 &&
		value.length === selectableOptions.length &&
		selectableOptions.every((option) =>
			value.some((selectedOption) => selectedOption.value === option.value)
		)

	const isOptionSelected = (option: MultiSelectOption) =>
		value.some(({ value }) => value === option.value) || isSelectAllSelected()

	const getOptions = () =>
		selectAllOption !== undefined ? [selectAllOption, ...options] : options

	const getValue = () => {
		return isSelectAllSelected() && selectAllOption !== undefined ? [selectAllOption] : value
	}
	const onSelectionChange = (
		newValue: MultiValue<MultiSelectOption>,
		actionMeta: ActionMeta<MultiSelectOption>
	) => {
		const { action, option, removedValue } = actionMeta
		if (
			maxAmountItems !== undefined &&
			value.length >= maxAmountItems &&
			action === "select-option"
		) {
			return
		}
		if (
			minAmountItems !== undefined &&
			value.length <= minAmountItems &&
			(action === "deselect-option" || action === "remove-value")
		) {
			return
		}
		if (
			action === "select-option" &&
			selectAllOption !== undefined &&
			option?.value === selectAllOption.value
		) {
			onChange(selectableOptions, actionMeta)
		} else if (
			(action === "deselect-option" &&
				selectAllOption !== undefined &&
				option?.value === selectAllOption.value) ||
			(action === "remove-value" &&
				selectAllOption !== undefined &&
				removedValue.value === selectAllOption.value)
		) {
			if (minAmountItems !== undefined && minAmountItems > 0) {
				return
			}
			onChange([], actionMeta)
		} else if (actionMeta.action === "deselect-option" && isSelectAllSelected()) {
			onChange(
				selectableOptions.filter(({ value }) => value !== option?.value),
				actionMeta
			)
		} else {
			onChange(
				(newValue ?? []).filter((option) => option.isDisabled !== true),
				actionMeta
			)
		}
	}

	return (
		<ReactSelect<MultiSelectOption, true, GroupBase<MultiSelectOption>>
			components={selectComponents}
			isDisabled={isDisabled}
			placeholder={placeHolderText}
			tabIndex={tabIndex}
			classNamePrefix="mySelect"
			isOptionSelected={isOptionSelected}
			options={getOptions()}
			value={getValue()}
			onChange={onSelectionChange}
			hideSelectedOptions={false}
			closeMenuOnSelect={false}
			isMulti
			isClearable={isClearable}
			styles={styles(getValue())}
			isSearchable={true}
			menuPortalTarget={document.body}
			onKeyDown={onKeyDown}
			isOptionDisabled={(option: MultiSelectOption) => option.isDisabled === true}
			filterOption={(candidate: { readonly data: MultiSelectOption }, searchText) =>
				matchesSelectableLabelSearch(searchText, candidate.data.label)
			}
		/>
	)
}

// const CustomOption = (props: OptionProps<ValueLabelPair & { deadweight?: string }>) => {
// 	const { data, innerRef, innerProps } = props
// 	return (
// 		<div
// 			ref={innerRef}
// 			{...innerProps}
// 			className="flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-gray-100"
// 		>
// 			<span>{data.label}</span>
// 			<span className="text-small ml-2 rounded-full bg-blue-600 px-2 py-0.5 text-white">
// 				{data.deadweight ?? "dwt: 10000 t"}
// 			</span>
// 		</div>
// 	)
// }
